<template>
  <div class="program-cards" :class="[$store.state.campaign, { light, 'no-padding-top': noPaddingTop, inline }]">
    <div class="container">
      <div class="heading col-10 offset-1">
        <h2 class="h2" :class="titleClass" v-html="title" />
        <p v-if="subtitle" class="subtitle">
          {{ subtitle }}
        </p>
      </div>
      <div class="modules row">
        <div
          v-for="(group, idx) in dataStructure" :key="idx" class="col"
          :class="col"
        >
          <div
            v-for="(module, i) in group" :key="i" class="module"
            :class="[{ show: modules.indexOf(module) === currentModule && $store.state.layout === 'mobile', expand: module.seeMore }, `card-${module.moduleId + 1}`]"
          >
            <div class="module-mobile" @click="changeModule(modules.indexOf(module))">
              <span v-if="$config.platform === 'ds' && !showCareerTracks">
                Module {{ modules.indexOf(module) + 1 }}
                <IconArrowDown />
              </span>
              <span v-else>
                Career track {{ modules.indexOf(module) + 1 }}
                <IconArrowDown />
              </span>
            </div>
            <div class="module-see-more">
              <div @click.prevent="module.seeMore = !module.seeMore; expandModule(!module.seeMore)">
                <span v-if="module.seeMore">See Less <IconArrowDown /></span>
                <span v-else class="expand">See More <IconArrowDown /></span>
              </div>
            </div>
            <div class="module-body">
              <p v-if="$config.platform === 'ds' && !showCareerTracks" class="subtitle">
                Module {{ module.moduleId + 1 }}
              </p>
              <p v-else class="subtitle">
                Career track {{ module.moduleId + 1 }}
              </p>
              <div class="module-heading">
                <h3 class="h3" :class="{ 'col-8': !noIcons }">
                  {{ module.title }}
                </h3>
                <component
                  :is="module.image" v-if="!noIcons" v-show="className !== 'affiliate'"
                  class="module-image"
                />
              </div>
              <p class="body-2">
                {{ module.subtitle }}
              </p>
              <div v-if="module.courses && module.courses.length" class="module-info">
                <div class="course">
                  <div v-for="(course, id) in module.courses" :key="id">
                    <div class="course-info">
                      <span class="number">{{ ('0' + getCourseId(modules.indexOf(module), course.name)).slice(-2) }}</span>
                      <span class="name" :title="course.name">{{ course.name }}</span>
                      <span class="lessons">{{ course.lessons.total || course.lessons }} lessons</span>
                    </div>
                    <hr>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="button" class="btn-lg btn-primary" :data-event="`${$route.path.replace(/\//g, '')}_modules.click`"
        @click.prevent="$emit('scrollToLastSection')"
      >
        {{ button.text }}
      </button>
    </div>
  </div>
</template>

<script>
import { merge } from 'lodash-es';
import IconArrowDown from '../../assets/svg/icon-expand.svg?vue-component';
import IconAdvanced from '../../assets/svg/icon-module-advanced-specialization.svg?vue-component';
import IconBusiness from '../../assets/svg/icon-module-business-specialization.svg?vue-component';
import IconDSFundamentals from '../../assets/svg/icon-module-ds-fundamentals.svg?vue-component';
import IconMachineLearning from '../../assets/svg/icon-module-machine-learning.svg?vue-component';
import IconProgrammingDS from '../../assets/svg/icon-module-programming-for-ds.svg?vue-component';
import IconTrackDataScientist from '../../assets/svg/icon-track-ds-data-scientist.svg?vue-component';
import IconTrackDataAnalyst from '../../assets/svg/icon-track-ds-data-analyst.svg?vue-component';
import IconTrackBusinessAnalyst from '../../assets/svg/icon-track-ds-business-analyst.svg?vue-component';

export default {
  components: {
    IconArrowDown,
    IconAdvanced,
    IconBusiness,
    IconDSFundamentals,
    IconMachineLearning,
    IconProgrammingDS,
    IconTrackDataScientist,
    IconTrackDataAnalyst,
    IconTrackBusinessAnalyst,
  },
  props: {
    light: Boolean,
    className: { type: String, default: '', required: false },
    modulesExtend: { type: Object, default: () => ({}), required: false },
    title: { type: String, default: 'Experience a Structured Way of Learning', required: false },
    titleClass: { type: String, default: '', required: false },
    subtitle: { type: String, default: '', required: false },
    button: { type: [Object, Boolean], required: false, default: null },
    noPaddingTop: Boolean,
    showCareerTracks: Boolean,
    col: { type: String, default: 'col-6' },
    inline: Boolean,
    noIcons: Boolean,
  },
  data() {
    return {
      currentModule: 0,
      modules: this.modulesInitialData(),
    };
  },
  async fetch() {
    let modulesData;

    if (this.$config.platform === 'ds' && !this.showCareerTracks) {
      modulesData = await this.$axios.$get('/modules').catch(() => ([]));
    } else {
      modulesData = await this.$axios.$get('/career-tracks?landingPage=1').catch(() => ([]));
    }

    // eslint-disable-next-line no-return-assign
    modulesData.map((m, id) => {
      if (!this.modules[id]) {
        return true;
      }

      // extend module data
      if (this.modulesExtend[id]) {
        this.modules[id] = merge(this.modules[id], this.modulesExtend[id]);
      }

      this.modules[id].courses = m.courses;
      this.modules[id].title = m.name;

      return true;
    });
  },
  computed: {
    dataStructure() {
      let cols = 1;
      if (this.$store.state.layout === 'desktop') {
        cols = 2;
      }
      const data = this.modules || {};
      const dataArray = [];
      const dataArrayLeft = [];
      const dataArrayRight = [];

      if (this.inline) {
        for (let i = 0; i < data.length; i += 1) {
          if (!dataArray[i]) {
            dataArray[i] = [];
          }
          dataArray[i].push(data[i]);
        }
      } else if (cols === 1) {
        for (let i = 0; i < data.length; i += 1) {
          if (!dataArray[0]) {
            dataArray[0] = [];
          }
          dataArray[0].push(data[i]);
        }
      } else {
        for (let i = 0; i < data.length; i += 1) {
          if (i % 2 === 0) {
            dataArrayRight.push(data[i]);
          } else {
            dataArrayLeft.push(data[i]);
          }
        }
        dataArray.push(dataArrayRight);
        dataArray.push(dataArrayLeft);
      }
      return dataArray;
    },
  },
  methods: {
    getCourseId(module, name) {
      return this.modules[module].courses.findIndex((course) => course.name === name) + 1;
    },
    changeModule(module) {
      this.currentModule = module;
      this.modules[module].seeMore = false;
      if (this.$store.state.layout === 'mobile') {
        setTimeout(() => {
          const headerHeight = 68;
          const buffer = 25;
          const scrollToEl = document.querySelector('.module.show');
          const topOfElement = window.pageYOffset + scrollToEl.getBoundingClientRect().top - headerHeight - buffer;
          window.scroll({ top: topOfElement, behavior: 'smooth' });
        }, 200);
      }
    },
    expandModule(show) {
      if (this.$store.state.layout === 'mobile') {
        const headerHeight = 68;
        const buffer = 25;
        let scrollToEl;
        if (show) {
          scrollToEl = document.querySelector('.module.show');
        } else {
          scrollToEl = document.querySelector('.module.show .module-info');
        }
        const topOfElement = window.pageYOffset + scrollToEl.getBoundingClientRect().top - headerHeight - buffer;
        window.scroll({ top: topOfElement, behavior: 'smooth' });
      }
    },
    modulesInitialData() {
      if (this.$config.platform === 'ds' && !this.showCareerTracks) {
        return [
          {
            title: 'Data Science Fundamentals',
            subtitle: 'This module is optimized to help you develop a strong foundation for a data science career. It dives deep into the core principles of probability, statistics, and mathematics necessary for building machine and deep learning models further in the program. You will start working with data and learn how to visually present the results of your analyses. ',
            image: 'IconDSFundamentals',
            seeMore: false,
            moduleId: 0,
          },
          {
            title: 'Programming for Data Science',
            subtitle: 'Here you will focus on developing a versatile programming skillset. You will acquire a thorough functional understanding of relational databases, using essential SQL queries to preprocess data, as well as coding and leveraging popular libraries in Python, like NumPy and matplotlib. You will also learn the best ways to manipulate and visualize data in R. ',
            image: 'IconProgrammingDS',
            seeMore: false,
            moduleId: 1,
          },
          {
            title: 'Machine & Deep Learning in Python',
            subtitle: 'Building on the foundations developed in the first 2 modules, Module 3 will teach you how to apply advanced statistical methods to execute predictive analytics. You will learn how to use sklearn to build complete linear and logistic regression models from scratch, and how to cluster unlabeled datasets with k-means. You will then progress to building deep learning models with the Keras library in TensorFlow 2.0, optimizing your neural network with backpropagation and fine-tuning your model.  ',
            image: 'IconMachineLearning',
            seeMore: false,
            moduleId: 2,
          },
          {
            title: 'Advanced Specialization',
            subtitle: 'By now, you will have developed a solid understanding of Python programming and statistical modeling. The concluding module gives you the opportunity to mold your data science expertise according to a field of your choosing. From developing next-gen fintech products to helping retail giants boost profitability through customer analytics, you’ll be able to make a valuable contribution to a diverse industry spectrum. ',
            image: 'IconAdvanced',
            seeMore: false,
            moduleId: 3,
          },
          {
            title: 'Business Specialization',
            subtitle: 'Aside from the technical capabilities, you need to have strong business acumen to excel on the job. The courses in this module provide invaluable business preparation that few realize they need to break into data science. Any data scientist worth their salt should understand what drives value for a company—including identifying key performance indicators, how business variables are interrelated, and what makes sense from a strategic point of view. You should be able to extract meaningful insights from data to recommend actionable adjustments that create long-term benefits.',
            image: 'IconBusiness',
            seeMore: false,
            moduleId: 4,
          },
        ];
      }
      if (this.$config.platform === 'ds') {
        return [
          {
            title: 'Data Scientist',
            subtitle: 'Build your data science skillset from the ground up starting with the maths and statistics intuition behind effective data-fueled solutions. Before upskilling in the most in-demand programming language today and diving into Python’s machine learning capabilities. Top off your data science proficiency with relational database management in SQL and deep learning in TensorFlow 2. With a career roadmap from a renowned industry expert and a primer on data product management, you will easily stand out from the data crowd.',
            image: 'IconTrackDataScientist',
            seeMore: false,
            moduleId: 0,
          },
          {
            title: 'Data Analyst',
            subtitle: 'Master the ins and outs of data analytics from foundational topics in data management with Excel and SQL to data cleansing and preprocessing using Python’s highly sought-after functionalities – Pandas and Numpy. Complete your analytics toolkit with the full range of data visualization tools you’ll need to become a compelling data storyteller. Featuring insights into the SQL interview process and a tutorial on date and time value manipulation in Python to give you a strong leg up over the competition.',
            image: 'IconTrackDataAnalyst',
            seeMore: false,
            moduleId: 1,
          },
          {
            title: 'Business Analyst',
            subtitle: 'Harness the full power of data for business starting with use-case-driven literacy and strategy courses that will enable you to devise and evangelize for data-fueled growth solutions within any organization. Complement your analytic rigor with the technical know-how needed for preparing and visualizing reports using popular tools such as Excel Pivot Tables, Tableau, R, and Python. The elective courses in this track are designed to help you get an edge in business intelligence with practice-heavy introductions to project management and Power BI.',
            image: 'IconTrackBusinessAnalyst',
            seeMore: false,
            moduleId: 2,
          },
        ];
      }
      return [
        {
          title: 'Financial Analyst',
          subtitle: 'Learn everything you need to build a successful financial analysis career. Acquire robust accounting, financial planning, analysis, and reporting skills.',
          image: 'IconDSFundamentals',
          seeMore: false,
          moduleId: 0,
        },
        {
          title: 'Investment Banker',
          subtitle: 'Prepare for this demanding career by learning financial modeling, investment banking, and corporate finance.',
          image: 'IconMachineLearning',
          seeMore: false,
          moduleId: 1,
        },
        {
          title: 'Investment Analyst',
          subtitle: 'Gain the skills to research and assess potential investment opportunities and become a sought-after investment analyst or associate.',
          image: 'IconProgrammingDS',
          seeMore: false,
          moduleId: 2,
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.program-cards {
  &.cfp {
    padding-bottom: 120px;
  }

  &:not(.no-padding-top) {
    padding-top: 120px;
  }

  .module-heading .h3 {
    margin-bottom: var(--spacing-24);
  }

  &.light {
    .modules {
      .module {
        background-color: var(--main-light);

        .module-see-more:after {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0) -13.64%, #FFF 66.26%);
        }

        .subtitle,
        .body-2 {
          color: var(--main-dark-shade-3);
        }

        .module-info {
          .course {
            > div {
              .course-info {
                .lessons {
                  color: var(--main-dark-shade-3);
                }
              }

              hr:after {
                color: rgba(0, 12, 31, 0.24);
              }
            }
          }
        }
      }
    }
  }

  .heading {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: var(--spacing-36);
  }

  .modules {
    .col-6:nth-of-type(2) {
      margin-top: 90px;
    }

    .module {
      position: relative;
      padding: var(--spacing-55) var(--spacing-50);
      background-color: var(--main-dark-shade-1);
      border-radius: 7px;
      max-height: 740px;
      transition: all var(--duration) ease;
      overflow: hidden;

      &:not(:last-of-type) {
        margin-bottom: var(--spacing-32);
      }

      &.expand {
        max-height: 1500px;
        padding-bottom: var(--spacing-100);

        .module-body {
          .module-info .course {
            > div:last-of-type {
              hr {
                display: none;
              }
            }
          }
        }

        .module-see-more:after {
          background: none;
        }
      }

      .module-mobile {
        display: none;
      }

      .module-see-more {
        min-height: 20px;
        color: var(--accent);
        margin-top: 10px;
        position: absolute;
        bottom: 20px;
        left: 0;
        width: 100%;
        display: flex;

        > div {
          z-index: 1;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
        }

        span {
          font-weight: 600;
          z-index: 1;

          &:not(.expand) {
            svg {
              transform: rotate(180deg);
            }
          }

          svg {
            margin-left: 10px;

            path[fill] {
              fill: var(--accent);
            }
          }
        }

        &:after {
          content: '';
          position: absolute;
          top: -80px;
          height: 120px;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(180deg, rgba(20, 31, 49, 0) -13.64%, #141F31 66.26%);
        }
      }

      .subtitle {
        font-size: 18px;
        color: var(--main-light-shade-4);
        margin: 0 0 6px 0;
      }

      .body-2 {
        color: var(--main-dark-shade-4);
        margin-bottom: var(--spacing-48);
      }

      &.card-1 {
        .module-image {
          position: absolute;
          top: 35px;
          right: 0;
          height: 139px;
        }
      }

      &.card-2 {
        .module-image {
          position: absolute;
          top: 40px;
          right: 6px;
          height: 139px;
        }
      }

      &.card-3 {
        .module-image {
          position: absolute;
          top: 24px;
          right: 0;
          height: 156px;
        }
      }

      &.card-4 {
        .module-image {
          position: absolute;
          top: 42px;
          right: 0;
          height: 131px;
        }
      }

      &.card-5 {
        .module-image {
          position: absolute;
          top: 42px;
          right: 0;
          height: 131px;
        }
      }

      .module-info {
        display: flex;
        flex-wrap: wrap;

        .course {
          width: 100%;

          > div {
            margin-bottom: 18px;

            hr {
              border: none;
              margin: 0;
            }

            hr:after {
              content: '....................................................................................';
              letter-spacing: 15px;
              font-size: 17px;
              color: rgba(255, 255, 255, 0.24);
            }

            .course-info {
              display: flex;
              padding-bottom: 2px;

              .number {
                color: var(--accent);
                margin-right: 11px;
              }

              .name {
                font-weight: 500;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 60%;
                margin-right: 11px;
              }

              .lessons {
                color: var(--main-dark-shade-4);
                margin-left: auto;
                flex: 0 0 auto;
              }
            }
          }
        }
      }
    }
  }

  .btn-primary {
    width: 350px;
    height: 65px;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: var(--spacing-60) auto;
  }

  @include bp($bp-mobile) {
    &.light {
      .modules {
        .module {
          &.show {
            background-color: var(--main-light);
          }

          .module-mobile {
            background-color: var(--main-light);
            color: var(--main-dark-shade-3);
          }
        }
      }
    }

    .heading {
      margin-bottom: var(--spacing-32);

      .h2 {
        margin-bottom: 0;
      }
    }

    .modules {
      .module {
        margin-top: var(--spacing-12);
        margin-bottom: var(--spacing-12);
        padding: 0;
        background: none;
        display: block;
        position: relative;

        &:not(:last-of-type) {
          margin-top: 0;
          margin-bottom: var(--spacing-10);
        }

        &:last-of-type {
          margin-top: 0;
        }

        &:not(.show) {
          height: 68px;
          overflow: hidden;

          .module-see-more {
            display: none;
          }

          .module-image {
            display: none;
          }
        }

        &.show {
          display: block;
          max-height: 700px;
          padding: var(--spacing-60) var(--spacing-18) var(--spacing-30);
          overflow: hidden;
          background-color: var(--main-dark-shade-1);
          border-radius: 7px;

          .module-mobile {
            background: none;
            cursor: initial;
            height: auto;
            margin-bottom: var(--spacing-20);
            justify-content: flex-start;

            span {
              color: var(--main-light);

              svg {
                display: none;
              }
            }
          }
        }

        &.expand {
          max-height: 2000px;
          padding-bottom: 0;
        }

        &.show.expand {
          padding-bottom: var(--spacing-100);
        }

        .module-mobile {
          height: 68px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 21px;
          background: var(--main-dark-shade-2);
          border-radius: 7px;
          color: #BDBDBD;
          cursor: pointer;

          span {
            display: flex;
            justify-content: center;
            align-items: center;

            svg {
              margin-left: 10px;

              path[fill] {
                fill: #BDBDBD;
              }
            }
          }
        }

        .module-body {
          .subtitle {
            display: none;
          }

          .h3 {
            max-width: 100%;
            font-size: 20px;
          }

          .module-info {
            .course {
              padding-right: 0 !important;
              padding-left: 0 !important;

              > div {
                .name {
                  max-width: 100%;
                }

                .lessons {
                  display: none;
                }

                &:last-of-type hr {
                  display: block;
                }
              }
            }
          }
        }
      }
    }
  }

  &.inline {
    .modules .module {
      padding: 32px;

      .h3 {
        font-size: 28px;
      }

      .module-info .course > div {
        margin-bottom: 12px;
      }
    }
  }
}

@include bp($bp-laptop) {
  .program-cards {
    .modules {
      .module {
        &.card-1,
        &.card-2,
        &.card-3,
        &.card-4 {
          .module-image {
            top: 50px;
            height: 119px;
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .program-cards {
    padding-top: var(--spacing-60);
    padding-bottom: var(--spacing-60);

    .modules {
      .module {
        &.show {
          padding: var(--spacing-60) var(--spacing-18) var(--spacing-30);
        }

        .module-body {
          .module-heading {
            display: flex;
            align-items: center;

            h3 {
              max-width: 50%;
            }
          }

          .module-image {
            height: 78px;
          }
        }

        &.card-1,
        &.card-2,
        &.card-3,
        &.card-4 {
          .module-image {
            top: 50px;
          }
        }
      }
    }

    .btn-primary {
      width: 100%;
      max-width: 100%;
    }
  }
}

.fa {
  @import '../../assets/sass/fa/components/program-cards.scss';
}
</style>
